var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/lib/helpers/roundUpTo2Digits.ts
var roundUpTo2Digits = (value) => {
  return Math.round(value * 100) / 100;
};

// src/lib/calculator/Calculator.ts
var Calculator = class {
  constructor(config) {
    this.config = config;
  }
  isFreeTicket(ticket) {
    return ticket.price + ticket.venueFee === 0;
  }
  includeFeesInPriceForOrder(order) {
    return order.tickets.some((ticket) => ticket.includeFeesInPrice);
  }
  /**
   * Returns the total price of all tickets in the given order.
   * @param tickets The tickets to sum up
   * @returns The total price of all tickets
   */
  getTicketsSubtotal(tickets) {
    return tickets.reduce((total, ticket) => total + ticket.price, 0);
  }
  /**
   * Returns the total platform fee for the given order. This is the cost of doing business with the platform.
   * @param order The order to calculate the platform fee for
   * @returns The total platform fee
   */
  getPlatformFee(order) {
    let fee = 0;
    for (const ticket of order.tickets) {
      if (!this.isFreeTicket(ticket) && !this.getTicketIsFullyDiscounted(order, ticket))
        fee += this.config.platformFee.amount;
    }
    return fee;
  }
  /**
   * Returns the total venue fee for the given order.
   * @param order The order to calculate the venue fee for
   * @returns The total venue fee
   */
  getVenueFee(order) {
    return order.tickets.filter(
      (t) => !this.isFreeTicket(t) && !this.getTicketIsFullyDiscounted(order, t)
    ).reduce((total, ticket) => total + ticket.venueFee, 0);
  }
  /**
   * Calculates the discount amount for a single ticket. This is the amount of money the customer should get back for that ticket.
   * @param order The order to calculate the discount for
   * @param ticket The ticket to calculate the discount for
   * @returns The discount amount for the ticket
   */
  getTicketDiscountAmount(order, ticket) {
    var _a, _b;
    if (!order.discount) return 0;
    const breakdown = order.discount.applyToOrder(order.tickets);
    return (_b = (_a = breakdown.ticketBreakdown.find((t) => t.originalTicket.id === ticket.id)) == null ? void 0 : _a.discountedAmount) != null ? _b : 0;
  }
  /**
   * Determines if a ticket in the given order is fully discounted.
   *
   * @param order The order to check
   * @param ticket The ticket to check
   * @returns true if the ticket is fully discounted, false otherwise
   */
  getTicketIsFullyDiscounted(order, ticket) {
    if (!order.discount) return false;
    const breakdown = order.discount.applyToOrder(order.tickets);
    const ticketDiscount = breakdown.ticketBreakdown.find(
      (t) => t.originalTicket.id === ticket.id
    );
    if (!ticketDiscount) return false;
    return ticketDiscount.isFullyDiscounted || ticketDiscount.discountedAmount >= ticket.price;
  }
  /**
   * Calculates the total discount amount for the given order.
   *
   * This function applies the discount to the order's tickets and retrieves
   * the total discounted amount from the discount breakdown.
   *
   * @param order The order to calculate the total discount for
   * @returns The total discount amount for the order
   */
  getTotalDiscountAmount(order) {
    var _a, _b;
    const breakdown = (_a = order.discount) == null ? void 0 : _a.applyToOrder(order.tickets);
    return (_b = breakdown == null ? void 0 : breakdown.totalDiscountedAmount) != null ? _b : 0;
  }
  /**
   * Calculates the total processing fee for all tickets in the given order.
   *
   * This fee is determined by calculating the percentage of the order's total
   * that is attributed to the tickets (including any platform and venue fees if
   * not included in the price), and then applying this percentage to the
   * processing fee for the entire order.
   *
   * @param order The order to calculate the tickets processing fee for
   * @returns The total processing fee for all tickets in the order
   */
  getTotalTicketsProcessingFee(order) {
    const totalWithoutProcessingFee = this.getTotalWithoutProcessingFee(order);
    if (totalWithoutProcessingFee === 0) return 0;
    const includeFees = this.includeFeesInPriceForOrder(order);
    const ticketFees = this.getPlatformFee(order) + this.getVenueFee(order);
    const totalTicketsPercentage = (this.getTicketsSubtotal(order.tickets) - this.getTotalDiscountAmount(order) + (includeFees ? 0 : ticketFees)) / totalWithoutProcessingFee;
    return totalTicketsPercentage * this.getProcessingFeeForOrder(order);
  }
  /**
   * Calculates the processing fee amount for a single ticket. This is the amount of money Stripe takes as a processing fee for that ticket.
   * @param order The order to calculate the processing fee for
   * @param ticket The ticket to calculate the processing fee for
   * @returns The processing fee amount for the ticket
   */
  getTicketProcessingFee(order, ticket) {
    if (this.isFreeTicket(ticket) || this.getTicketIsFullyDiscounted(order, ticket)) {
      return 0;
    }
    const ticketDiscountAmount = this.getTicketDiscountAmount(order, ticket);
    let ticketPercentage;
    if (this.includeFeesInPriceForOrder(order)) {
      ticketPercentage = (ticket.price - ticketDiscountAmount) / this.getTotalWithoutProcessingFee(order);
    } else {
      ticketPercentage = (ticket.price + ticket.venueFee + this.config.platformFee.amount - ticketDiscountAmount) / this.getTotalWithoutProcessingFee(order);
    }
    return ticketPercentage * this.getProcessingFeeForOrder(order);
  }
  /**
   * Calculates the platform fee amount for a single ticket.
   * @param order The order to calculate the platform fee for
   * @param ticket The ticket to calculate the platform fee for
   * @returns The platform fee amount for the ticket
   */
  getTicketPlatformFee(order, ticket) {
    if (this.isFreeTicket(ticket) || this.getTicketIsFullyDiscounted(order, ticket)) {
      return 0;
    }
    return this.config.platformFee.amount;
  }
  /**
   * Calculates the venue fee amount for a single ticket.
   * @param order The order to calculate the venue fee for
   * @param ticket The ticket to calculate the venue fee for
   * @returns The venue fee amount for the ticket
   */
  getTicketVenueFee(order, ticket) {
    if (this.isFreeTicket(ticket) || this.getTicketIsFullyDiscounted(order, ticket))
      return 0;
    return ticket.venueFee;
  }
  /**
   * Calculates the refund amount for a single ticket. This is the amount of money the customer should get back for that ticket.
   * It is the same as the amount paid for the ticket.
   * @param order The order to calculate the refund for
   * @param ticket The ticket to calculate the refund for
   * @returns The refund amount for the ticket
   */
  getTicketRefundAmount(order, ticket) {
    return this.getAmountPaidForTicket(order, ticket);
  }
  /**
   * Calculates the amount of money that the customer paid for a single ticket in the order.
   * @param order The order to calculate the paid amount for
   * @param ticket The ticket to calculate the paid amount for
   * @returns The amount the customer paid for this ticket
   */
  getAmountPaidForTicket(order, ticket) {
    const ticketDiscountAmount = this.getTicketDiscountAmount(order, ticket);
    const fullyDiscounted = ticketDiscountAmount >= ticket.price;
    if (fullyDiscounted) return 0;
    if (this.includeFeesInPriceForOrder(order)) {
      return ticket.price - ticketDiscountAmount;
    }
    const ticketPercentage = (ticket.price + ticket.venueFee + this.config.platformFee.amount - ticketDiscountAmount) / this.getTotalWithoutProcessingFee(order);
    return ticketPercentage * this.getTotalToPay(order);
  }
  /**
   * Calculates the raw price of a ticket in the given order.
   *
   * This price does not include any additional fees such as platform or venue fees,
   * and excludes fees if the fees are included in the ticket price.
   *
   * @param order The order to calculate the raw ticket price for
   * @param ticket The ticket to calculate the raw price for
   * @returns The raw price of the ticket
   */
  getRawPriceForTicket(order, ticket) {
    if (!this.includeFeesInPriceForOrder(order)) {
      return ticket.price;
    }
    if (this.getTicketIsFullyDiscounted(order, ticket)) {
      return ticket.price;
    }
    return roundUpTo2Digits(
      roundUpTo2Digits(ticket.price) - roundUpTo2Digits(this.config.platformFee.amount) - roundUpTo2Digits(ticket.venueFee) - roundUpTo2Digits(this.getTicketProcessingFee(order, ticket))
    );
  }
  /**
   * Returns the total price of all addons in the given order.
   * @param addons The addons to sum up
   * @returns The total price of all addons
   */
  getAddonsSubtotal(addons) {
    return addons.reduce((total, addon) => total + addon.price, 0);
  }
  /**
   * Returns the total tax amount for all addons in the given order.
   * @param addons The addons to sum up
   * @returns The total tax amount for all addons
   */
  getAddonsTax(addons) {
    return addons.reduce(
      (total, addon) => total + addon.price * addon.tax / 100,
      0
    );
  }
  /**
   * Returns the tax amount for a single addon in the given order.
   * @param order The order to calculate the tax for
   * @param addon The addon to calculate the tax for
   * @returns The tax amount for the addon
   */
  getAddonTax(order, addon) {
    return addon.price * (addon.tax / 100);
  }
  /**
   * Calculates the processing fee amount for a single addon. This is the amount of money Stripe takes as a processing fee for that addon.
   * @param order The order to calculate the processing fee for
   * @param addon The addon to calculate the processing fee for
   * @returns The processing fee amount for the addon
   */
  getAddonProcessingFee(order, addon) {
    const addonPercentage = (addon.price + this.getAddonTax(order, addon)) / this.getTotalWithoutProcessingFee(order);
    return addonPercentage * this.getProcessingFee(this.getTotalWithoutProcessingFee(order));
  }
  /**
   * Calculates the refund amount for a single addon.
   *
   * The refund amount is the amount the customer paid for the addon.
   * @param order The order the addon is part of
   * @param addon The addon to calculate the refund for
   * @returns The refund amount for the addon
   */
  getAddonRefundAmount(order, addon) {
    return this.getAmountPaidForAddon(order, addon);
  }
  /**
   * Calculates the refund amount for the price of a single addon.
   *
   * This amount is the amount the customer paid for the addon, without its tax.
   * @param order The order the addon is part of
   * @param addon The addon to calculate the refund for
   * @returns The refund amount for the price of the addon
   */
  getAddonPriceRefundAmount(order, addon) {
    const addonPricePercentage = addon.price / this.getTotalWithoutProcessingFee(order);
    return addonPricePercentage * this.getTotalToPay(order);
  }
  /**
   * Calculates the tax refund amount for a single addon.
   *
   * The tax refund amount is based on the proportion of the addon's tax relative
   * to the total order amount without the processing fee, and is applied to the
   * total amount the customer needs to pay.
   * @param order The order the addon is part of
   * @param addon The addon to calculate the tax refund for
   * @returns The tax refund amount for the addon
   */
  getAddonTaxRefundAmount(order, addon) {
    const addonTaxPercentage = this.getAddonTax(order, addon) / this.getTotalWithoutProcessingFee(order);
    return addonTaxPercentage * this.getTotalToPay(order);
  }
  /**
   * Calculates the amount the customer paid for a single addon.
   *
   * This amount includes the price of the addon, its tax, and its share of the processing fee.
   * @param order The order the addon is part of
   * @param addon The addon to calculate the amount for
   * @returns The amount the customer paid for the addon
   */
  getAmountPaidForAddon(order, addon) {
    const addonPercentage = (addon.price + this.getAddonTax(order, addon)) / this.getTotalWithoutProcessingFee(order);
    return addonPercentage * this.getTotalToPay(order);
  }
  /**
   * Calculates the share of the processing fee that belongs to the donation.
   *
   * If the order does not have a donation, this method returns 0.
   * @param order The order the donation is part of
   * @returns The share of the processing fee for the donation
   */
  getProcessingFeeForDonation(order) {
    if (!order.donation) return 0;
    const processingFee = this.getProcessingFeeForOrder(order);
    const donationPercentage = order.donation.amount / this.getTotalWithoutProcessingFee(order);
    return donationPercentage * processingFee;
  }
  /**
   * Calculates the amount the customer paid for the donation, including the share of the processing fee.
   *
   * @param order The order the donation is part of
   * @returns The amount the customer paid for the donation, including the share of the processing fee
   */
  getAmountPaidForDonation(order) {
    if (!order.donation) return 0;
    return order.donation.amount + this.getProcessingFeeForDonation(order);
  }
  /**
   * Calculates the total amount of money the customer paid for the given order,
   * without including the processing fee.
   *
   * This amount includes the subtotal of tickets, the venue fee, the platform fee,
   * the subtotal of addons, the tax of addons, and the discount (if any).
   * @param order The order to calculate the total for
   * @returns The total amount of money the customer paid, without the processing fee
   */
  getTotalWithoutProcessingFee(order) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const ticketSubtotal = this.getTicketsSubtotal(order.tickets);
    const venueFee = this.getVenueFee(order);
    const platformFee = this.getPlatformFee(order);
    const ticketFees = venueFee + platformFee;
    const addonsSubtotal = this.getAddonsSubtotal(order.addons);
    const addonsTax = this.getAddonsTax(order.addons);
    const discountedAmount = (_b = (_a = order.discount) == null ? void 0 : _a.applyToOrder(order.tickets).totalDiscountedAmount) != null ? _b : 0;
    if (discountedAmount >= ticketSubtotal) {
      return addonsTax + addonsSubtotal + ((_d = (_c = order.donation) == null ? void 0 : _c.amount) != null ? _d : 0);
    }
    if (this.includeFeesInPriceForOrder(order)) {
      return ticketSubtotal - discountedAmount + ((_f = (_e = order.donation) == null ? void 0 : _e.amount) != null ? _f : 0) + addonsSubtotal + addonsTax;
    }
    return ticketSubtotal - discountedAmount + ticketFees + addonsSubtotal + addonsTax + ((_h = (_g = order.donation) == null ? void 0 : _g.amount) != null ? _h : 0);
  }
  /**
   * Calculates the total amount of money the customer needs to pay for the given order.
   *
   * This amount includes the subtotal of tickets, the venue fee, the platform fee,
   * the subtotal of addons, the tax of addons, the discount (if any), and the processing fee.
   * @param order The order to calculate the total for
   * @returns The total amount of money the customer needs to pay
   */
  getTotalToPay(order) {
    const total = this.getTotalWithoutProcessingFee(order);
    const processingFee = this.getProcessingFeeForOrder(order);
    if (this.includeFeesInPriceForOrder(order)) {
      const ticketsProcessingFee = this.getTotalTicketsProcessingFee(order);
      return total + processingFee - ticketsProcessingFee;
    }
    return total + processingFee;
  }
  /**
   * Calculates the processing fee for a given amount of money.
   *
   * The processing fee is calculated using the following formula:
   *   fee = staticAmount + (percentage * amount)
   *   fee = staticAmount + (percentage * (amount + fee))
   *   ...
   *   fee = staticAmount + (percentage * (amount + fee)) (precision times)
   *
   * @param amount The amount of money to calculate the processing fee for
   * @param precision The precision to use to calculate the fee, defaults to the calculator's config precision
   * @returns The processing fee for the given amount
   */
  getProcessingFee(amount, precision = this.config.processingFeePrecision) {
    if (amount === 0) return 0;
    const processingFee = (n) => {
      if (n === 0) {
        return this.config.processingFee.staticAmount + this.config.processingFee.percentage * amount;
      }
      return this.config.processingFee.staticAmount + this.config.processingFee.percentage * (amount + processingFee(n - 1));
    };
    return processingFee(precision);
  }
  /**
   * Calculates the processing fee for a given order.
   *
   * This method simply calls `getProcessingFee` with the total amount of money the customer needs to pay without the processing fee.
   * @param order The order to calculate the processing fee for
   * @returns The processing fee for the given order
   */
  getProcessingFeeForOrder(order) {
    var _a;
    if (this.includeFeesInPriceForOrder(order)) {
      const onlyTicketsPurchased = order.addons.length === 0 && !((_a = order.donation) == null ? void 0 : _a.amount);
      return this.getProcessingFee(
        this.getTotalWithoutProcessingFee(order),
        onlyTicketsPurchased ? 0 : void 0
      );
    }
    return this.getProcessingFee(this.getTotalWithoutProcessingFee(order));
  }
};

// src/lib/calculator/processingFeePresets/online.ts
var onlineProcessingFeePreset = {
  staticAmount: 0.3,
  percentage: 0.029
};

// src/lib/calculator/processingFeePresets/terminal.ts
var terminalProcessingFeePreset = {
  staticAmount: 0.05,
  percentage: 0.027
};

// tests/helpers/helper.ts
function dollarFigureToNumber(dollarFigure) {
  if (!dollarFigure) return 0;
  return Number(dollarFigure.replace(/[^0-9.-]+/g, ""));
}
function initializeDiscount(discountType, applyTo, buyAmount, getAmount, discountAmount) {
  if (discountType == discounts_exports.DiscountTypes.BuyXGetYFreeDiscount) {
    return new discounts_exports.BuyXGetYFreeDiscount({
      buyAmount,
      freeAmount: getAmount
    });
  } else if (discountType === discounts_exports.DiscountTypes.PercentageDiscount) {
    return new discounts_exports.PercentageDiscount({ amount: getAmount / 100 });
  } else if (discountType === discounts_exports.DiscountTypes.SpendXGetYOffDiscount) {
    return new discounts_exports.SpendXGetYOffDiscount({
      spendAmount: buyAmount,
      offAmount: getAmount
    });
  } else if (discountType === discounts_exports.DiscountTypes.StaticDiscount) {
    return new discounts_exports.StaticDiscount({
      amount: discountAmount,
      appliedTo: applyTo
    });
  }
}
function getRefundAmounts(order) {
  const calculatorService = new Calculator({
    platformFee: { amount: 3 },
    processingFee: onlineProcessingFeePreset,
    processingFeePrecision: 3
  });
  const donationRefundAmount = calculatorService.getAmountPaidForDonation(order);
  return {
    donationRefundAmount,
    ticketsRefundAmounts: order.tickets.map((ticket) => {
      return {
        ticketId: ticket.id,
        ticketRefundAmount: calculatorService.getTicketRefundAmount(
          order,
          ticket
        )
      };
    }),
    orderRefundAmounts: order.addons.map((addon) => {
      return {
        addonId: addon.id,
        addonRefundAmount: calculatorService.getAddonRefundAmount(order, addon)
      };
    })
  };
}
function getPlatformFee(order) {
  const calculatorService = new Calculator({
    platformFee: { amount: 3 },
    processingFee: onlineProcessingFeePreset,
    processingFeePrecision: 3
  });
  const platformFee = calculatorService.getPlatformFee(order);
  return platformFee;
}
function getVenueFee(order) {
  const calculatorService = new Calculator({
    platformFee: { amount: 3 },
    processingFee: onlineProcessingFeePreset,
    processingFeePrecision: 3
  });
  const platformFee = calculatorService.getVenueFee(order);
  return platformFee;
}

// src/discounts.ts
var discounts_exports = {};
__export(discounts_exports, {
  BuyXGetYFreeDiscount: () => BuyXGetYFreeDiscount,
  Discount: () => Discount,
  DiscountTypes: () => DiscountTypes,
  PercentageDiscount: () => PercentageDiscount,
  SpendXGetYOffDiscount: () => SpendXGetYOffDiscount,
  StaticDiscount: () => StaticDiscount
});

// src/lib/discounts/Discount.ts
var DiscountTypes = /* @__PURE__ */ ((DiscountTypes2) => {
  DiscountTypes2["PercentageDiscount"] = "% Discount";
  DiscountTypes2["StaticDiscount"] = "$ Discount";
  DiscountTypes2["BuyXGetYFreeDiscount"] = "Buy X Get Y Free";
  DiscountTypes2["SpendXGetYOffDiscount"] = "Spend $X, Get $Y Off";
  return DiscountTypes2;
})(DiscountTypes || {});
var Discount = class {
  getTicketBreakdownForBasicDiscountAmount(tickets, discountAmount) {
    const subtotal = tickets.reduce((total, t) => total + t.price, 0);
    return tickets.map((ticket) => {
      if (ticket.price + ticket.venueFee === 0)
        return {
          originalTicket: ticket,
          discountedAmount: 0,
          isFullyDiscounted: false
        };
      const ticketPercentage = ticket.price / subtotal;
      const ticketDiscountAmount = discountAmount * ticketPercentage;
      const fullyDiscounted = ticketDiscountAmount >= ticket.price;
      return {
        originalTicket: ticket,
        discountedAmount: fullyDiscounted ? ticket.price : ticketDiscountAmount,
        isFullyDiscounted: fullyDiscounted
      };
    });
  }
};

// src/lib/discounts/StaticDiscount.ts
var StaticDiscount = class extends Discount {
  constructor(config) {
    super();
    this.config = config;
  }
  applyToOrder(tickets) {
    let discountAmount = 0;
    if (this.config.appliedTo === "reservation") {
      discountAmount = this.config.amount;
      return {
        totalDiscountedAmount: discountAmount,
        ticketBreakdown: this.getTicketBreakdownForBasicDiscountAmount(
          tickets,
          discountAmount
        )
      };
    }
    const ticketBreakdown = [];
    for (const ticket of tickets) {
      const fullyDiscounted = ticket.price <= this.config.amount;
      discountAmount += fullyDiscounted ? ticket.price : this.config.amount;
      ticketBreakdown.push({
        originalTicket: ticket,
        isFullyDiscounted: fullyDiscounted,
        discountedAmount: fullyDiscounted ? ticket.price : this.config.amount
      });
    }
    return {
      ticketBreakdown,
      totalDiscountedAmount: discountAmount
    };
  }
};

// src/lib/discounts/PercentageDiscount.ts
var PercentageDiscount = class extends Discount {
  constructor(config) {
    super();
    this.config = config;
    if (config.amount > 1) {
      throw new Error(
        "The percentage is expected to be a fraction percentage <= 1. You may need to use percentage / 100"
      );
    }
  }
  applyToOrder(tickets) {
    const subtotal = tickets.reduce((total, ticket) => total + ticket.price, 0);
    const discountAmount = this.config.amount * subtotal;
    return {
      ticketBreakdown: this.getTicketBreakdownForBasicDiscountAmount(
        tickets,
        discountAmount
      ),
      totalDiscountedAmount: discountAmount
    };
  }
};

// src/lib/discounts/BuyXGetYFreeDiscount.ts
var BuyXGetYFreeDiscount = class extends Discount {
  constructor(config) {
    super();
    this.config = config;
  }
  applyToOrder(tickets) {
    const x = this.config.buyAmount;
    const y = this.config.freeAmount;
    const doesNotQualifyForDiscount = tickets.filter((t) => t.price !== 0).length < x + y;
    if (doesNotQualifyForDiscount) {
      return {
        totalDiscountedAmount: 0,
        ticketBreakdown: tickets.map((t) => ({
          originalTicket: t,
          discountedAmount: 0,
          isFullyDiscounted: false
        }))
      };
    }
    const freedTickets = [];
    const sortedTickets = [...tickets].sort((a, b) => {
      if (a.price < b.price) return -1;
      return 1;
    });
    for (const ticket of sortedTickets) {
      if (ticket.price === 0) continue;
      freedTickets.push(ticket);
      if (freedTickets.length === y) break;
    }
    const discountAmount = freedTickets.reduce(
      (total, ticket) => total + ticket.price,
      0
    );
    return {
      ticketBreakdown: tickets.map((t) => {
        const isFreed = freedTickets.includes(t);
        return {
          discountedAmount: isFreed ? t.price : 0,
          isFullyDiscounted: isFreed,
          originalTicket: t
        };
      }),
      totalDiscountedAmount: discountAmount
    };
  }
};

// src/lib/discounts/SpendXGetYOffDiscount.ts
var SpendXGetYOffDiscount = class extends Discount {
  constructor(config) {
    super();
    this.config = config;
  }
  applyToOrder(tickets) {
    const subtotal = tickets.reduce((total, ticket) => total + ticket.price, 0);
    const discountActivated = subtotal > this.config.spendAmount;
    const aboveBaseline = subtotal - this.config.spendAmount;
    let discountAmount = 0;
    if (discountActivated) {
      discountAmount = Math.min(aboveBaseline, this.config.offAmount);
    }
    return {
      ticketBreakdown: this.getTicketBreakdownForBasicDiscountAmount(
        tickets,
        discountAmount
      ),
      totalDiscountedAmount: discountAmount
    };
  }
};
export {
  Calculator,
  discounts_exports as Discounts,
  dollarFigureToNumber,
  getPlatformFee,
  getRefundAmounts,
  getVenueFee,
  initializeDiscount,
  onlineProcessingFeePreset,
  terminalProcessingFeePreset
};
